import Vue from 'vue'
import dayjs from 'dayjs'

import * as C from '@shared/constants'
import { isPostCooldownOver } from '@shared/user-util'
import { sleep } from '@shared/utils'
import * as M from '@/store/mutations'
import * as _ from '@/lodash'
import api from '@/api'
import linkPreviewDemo from '@/assets/link-preview-demo'

const state = {
    data: [], // paginated data from EBS
    // NOTE: Once the channel's default listType is set, a watcher will
    // trigger the initial suggestion list fetch.
    listType: null,
    sortDir: C.SORT_DESC,
    offset: 0,
    hasMorePages: false,
    isLoadingSuggestions: false,
    replyToSuggestion: null, // The suggestion object being replied to.
    // The suggestion id the user is about to maybe place a bounty on.
    // The user can cancel the transaction at any time before confirmation.
    transactingBountySuggestionId: null,
    transactingBountyState: C.DEFAULT,
    // The transaction object from onTransactionComplete
    completedTransaction: null,
    usersLastSuggestion: {},
    now: null, // helper state to trigger time based getters
}

export const mutations = {
    [M.TOGGLE_LOADING_SUGGESTIONS] (state, payload) {
        state.isLoadingSuggestions = payload.isLoading
    },
    [M.SET_SUGGESTIONS] (state, { suggestions }) {
        state.data = suggestions
    },
    [M.ADD_SUGGESTIONS] (state, { suggestions }) {
        // Ignore suggestions that already exist in the state.data list.
        const data = _.differenceBy(suggestions, state.data, 'id')
        state.data.push(...data)
    },
    // prepend new post at the top of the list, reddit style
    [M.ADD_APPROVED_SUGGESTION] (state, { suggestion }) {
        state.data.unshift(suggestion)
    },
    [M.SET_LIST_FILTERS] (state, payload) {
        const { listType = state.listType, sortDir = state.sortDir } = payload

        state.listType = listType
        state.sortDir = sortDir
    },
    // This mutation is complicated because i'm merging a single emote
    // into aggregated emotes...
    [M.SET_EMOTE_REACTION] (state, payload) {
        const {
            suggestionId,
            userReacted,
            emote,
            alias,
        } = payload

        const suggestion = state.data.find(s => s.id === suggestionId)
        // Pubsub updates for a post we don't have. Just ignore
        if (!suggestion) { return }

        const { emotes = [] } = suggestion
        const index = emotes.findIndex(e => e.emote === emote)
        let reaction = emotes[index]

        // Quick sanity check, you can't react twice!!!!
        if (reaction?.userReacted && userReacted) {
            // debug('you cant react twice!')
            return
        }

        const isNewEmote = index === -1
        if (isNewEmote) {
            reaction = { emote, alias, count: 0 }
        } else {
            // debug('existing reaction!')
        }

        const countDiff = userReacted ? 1 : -1

        reaction.userReacted = userReacted
        reaction.count += countDiff
        // Check for existence. It's a boolean value. BEWARE FALSY CHECKS.
        if ('broadcasterReacted' in payload) {
            reaction.broadcasterReacted = payload.broadcasterReacted
        }

        if (isNewEmote) {
            // debug('adding reaction')
            emotes.push(reaction)
        } else if (reaction.count <= 0) {
            // debug('removing reaction')
            emotes.splice(index, 1)
        }
    },
    [M.UPDATE_SUGGESTION_STATUS] (state, { id, status }) {
        const suggestion = state.data.find(s => s.id === id)
        if (suggestion) {
            suggestion.status = status
        }
    },
    // Dont overuse
    [M.UPDATE_SUGGESTION] (state, payload) {
        const suggestion = state.data.find(s => s.id === payload.id)
        if (suggestion) {
            // Ensure reactive updates with Vue.set
            for (const [key, val] of Object.entries(payload)) {
                Vue.set(suggestion, key, val)
            }
        }
    },
    [M.SET_VOTE_TYPE] (state, payload) {
        const {
            suggestionId: id,
            voteType,
            previousVoteType,
            broadcasterVoteType,
        } = payload

        const suggestion = state.data.find(s => s.id === id)
        // Pubsub updates for a post we don't have. Just ignore
        if (!suggestion) { return }

        const s = suggestion
        if (voteType === C.VOTE_UP) {
            s.numUpvotes++
        } else if (voteType === C.VOTE_DOWN) {
            s.numDownvotes++
        }
        if (previousVoteType === C.VOTE_UP) {
            s.numUpvotes--
        } else if (previousVoteType === C.VOTE_DOWN) {
            s.numDownvotes--
        }

        // Pubsub user vote, don't change the current user's vote.
        if (payload.pubsubUserId) { return }

        s.userVoteType = voteType

        if (broadcasterVoteType) {
            s.broadcasterVoteType = broadcasterVoteType
        }
    },
    [M.SET_PAGINATION_INFO] (state, { offset = 0, hasMorePages = false }) {
        state.offset = offset
        state.hasMorePages = hasMorePages
    },
    [M.SET_NOW_TIME] (state, { now }) {
        state.now = now
    },
    [M.SET_USERS_LAST_SUGGESTION] (state, { suggestion }) {
        state.usersLastSuggestion = suggestion
    },
    [M.SET_REPLY_TO_SUGGESTION] (state, { suggestion }) {
        state.replyToSuggestion = suggestion
    },
    [M.DEMO_LINK_PREVIEW] (state) {
        state.data.unshift(linkPreviewDemo)
    },
    [M.SET_TRANSACTING_BOUNTY_STATE] (state, { state: bountyState, suggestionId }) {
        state.transactingBountyState = bountyState

        switch (bountyState) {
            case C.STATE_DEFAULT:
                state.transactingBountySuggestionId = null
                break
            case C.STATE_LOADING:
                state.transactingBountySuggestionId = suggestionId
                break
        }
    },
    [M.UPDATE_SUGGESTION_BOUNTY] (state, { bounty, suggestionId: id }) {
        const suggestion = state.data.find(s => s.id === id)
        if (!suggestion) { return }

        const existingBounty = suggestion.bounties.find(b => b.userId === bounty.userId)
        if (existingBounty) {
            existingBounty.totalAmount += bounty.totalAmount
        } else {
            suggestion.bounties.unshift(bounty)
        }
    },
    [M.SET_COMPLETED_TRANSACTION] (state, { transaction }) {
        state.completedTransaction = Object.freeze(transaction)
    },
    // Dont overuse.
    [M.UPDATE_SUGGESTION_MODULE] (state, update) {
        Object.assign(state, update)
    },
}

export const actions = {
    async fetchSuggestions ({ state, commit, getters }, payload) {
        commit(M.TOGGLE_LOADING_SUGGESTIONS, { isLoading: true })
        const PAGE_LIMIT = 10
        const pl = payload

        const mutation = pl?.mutation || M.SET_SUGGESTIONS
        const limit = pl?.limit || PAGE_LIMIT
        const listType = pl?.listType || state.listType
        const sortDir = pl?.sortDir || state.sortDir

        let { offset } = state
        if (mutation === M.SET_SUGGESTIONS) {
            offset = 0
            commit(M.SET_PAGINATION_INFO, { offset })
        }

        const { channelId } = getters
        const url = `channels/${channelId}/suggestions`
        const opts = {
            params: { listType, offset, limit, sortDir }
        }
        const minLoadingDuration = 350

        try {
            const promises = [api.get(url, opts), sleep(minLoadingDuration)]
            const [res] = await Promise.all(promises)
            const { suggestions } = res.data

            commit(mutation, { suggestions })
            commit(M.SET_PAGINATION_INFO, {
                offset: offset + PAGE_LIMIT,
                // Not a 100% accurate check, but good enough.
                hasMorePages: suggestions.length === PAGE_LIMIT,
            })
        } finally {
            commit(M.TOGGLE_LOADING_SUGGESTIONS, { isLoading: false })
        }
    },
    async postSuggestion ({ getters, rootState, commit }, payload) {
        const { channelId, userLanguage } = getters
        const userProps = ['id', 'name', 'login', 'profileImg', 'role']
        const user = _.pick(rootState.user, userProps)

        try {
            const url = `channels/${channelId}/suggestions`
            const data = {
                text: payload.suggestion,
                user,
                language: userLanguage
            }
            const res = await api.post(url, data)

            const { suggestion } = res.data

            commit(M.SET_USERS_LAST_SUGGESTION, { suggestion })
            commit(M.SET_CHANNEL_NOTIFICATION_READ_DATE,
                { date: new Date(suggestion.createdAt) })

            if (suggestion.status === C.STATUS_APPROVED) {
                commit(M.ADD_APPROVED_SUGGESTION, { suggestion })
            }
            return suggestion.status
        } catch (err) {
            Sentry.captureException(err)
            return err.response?.data?.status
        }
    },
    async saveVote ({ state, getters, commit }, payload) {
        const { channelId, isBroadcaster } = getters
        const { suggestionId: id, voteType } = payload
        if (isBroadcaster) {
            payload.broadcasterVoteType = voteType
        }
        commit(M.SET_VOTE_TYPE, payload)

        const url = `channels/${channelId}/suggestions/${id}/votes`
        await api.put(url, { voteType })
    },
    async updateSuggestionStatus ({ state, getters, commit }, { id, status }) {
        const { channelId } = getters
        const url = `channels/${channelId}/suggestions/${id}/status`
        await api.put(url, { status })

        commit(M.UPDATE_SUGGESTION_STATUS, { id, status })
    },
    async updateSuggestionReply ({ state, getters, commit }, payload) {
        const { channelId } = getters
        const { suggestionId: id, text, createdAt } = payload

        const url = `channels/${channelId}/suggestions/${id}/reply`
        const data = { text }
        if (createdAt) {
            data.createdAt = createdAt
        }
        const res = await api.put(url, data)

        const { reply } = res.data
        commit(M.UPDATE_SUGGESTION, { id, reply })
        // Turns off reply mode.
        commit(M.SET_REPLY_TO_SUGGESTION, { suggestion: null })
    },
    async updateSuggestionCompletedAt ({ state, getters, commit }, payload) {
        const { channelId } = getters
        const { id, completedAt } = payload

        const url = `channels/${channelId}/suggestions/${id}/completed`
        await api.put(url, { completedAt })

        commit(M.UPDATE_SUGGESTION, { id, completedAt })
    },
    async confirmCompletion ({ state, getters, commit }, payload) {
        const { channelId } = getters
        const { id } = payload

        const url = `channels/${channelId}/suggestions/${id}/confirm-completed`
        const res = await api.put(url)
        const { completionConfirmedAt } = res.data

        commit(M.UPDATE_SUGGESTION, { id, completionConfirmedAt })
    },
    async saveBountyTransaction ({ state, rootState, getters, commit }, payload) {
        // If this id is ever null, we're in trouble. We won't know which
        // suggestion the user placed a bounty on.
        const { transactingBountySuggestionId: id } = state
        const { channelId } = getters
        const { transaction } = payload

        // Transaction fired in another tab. Ignore.
        if (id === null) { return }
        if (transaction.initiator !== 'current_user') { return }

        transaction.profileImg = rootState.user.profileImg
        transaction.login = rootState.user.login

        commit(M.SET_COMPLETED_TRANSACTION, { transaction })

        let res
        try {
            const url = `channels/${channelId}/suggestions/${id}/bounty`
            res = await api.put(url, { transaction })
        } catch (err) {
            commit(M.SET_TRANSACTING_BOUNTY_STATE, { state: C.STATE_ERROR })
            return
        }
        const { bounty } = res.data
        commit(M.UPDATE_SUGGESTION_BOUNTY, { bounty, suggestionId: id })
        commit(M.SET_TRANSACTING_BOUNTY_STATE, { state: C.STATE_SUCCESS })
    },
}

const getters = {
    suggestionList: state => state.data,
    hasSuggestions: state => state.data.length > 0,
    hasMorePages: state => state.hasMorePages,
    listType: state => state.listType,
    sortDir: state => state.sortDir,
    isPendingList: state => state.listType === C.LIST_TYPE_PENDING,
    isArchivedList: state => state.listType === C.LIST_TYPE_ARCHIVED,
    isLoadingSuggestions: state => state.isLoadingSuggestions,
    transactingBountySuggestionId: state => state.transactingBountySuggestionId,
    transactingBountyState: state => state.transactingBountyState,
    completedTransaction: state => state.completedTransaction,
    lastSuggestionDate: state => {
        const date = state.usersLastSuggestion?.createdAt
        return date ? dayjs(date).toDate() : null
    },
    isAllowedToSuggest (state, getters, rootState) {
        if (getters.isBroadcaster) { return true }
        if (!getters.isIdentityLinkedUser) { return false }

        // eslint-disable-next-line no-unused-vars
        const now = state.now // HACK: force getter to update
        const { postCooldownMinutes } = rootState.channel
        return isPostCooldownOver(getters.lastSuggestionDate, postCooldownMinutes)
    },
    cooldownTimeRemainingStr: (state, getters, rootState) => {
        const now = state.now // HACK: force getter to update

        const dateUserCanPost = dayjs(getters.lastSuggestionDate)
            .add(rootState.channel.postCooldownMinutes, 'minute')

        const t = dayjs.duration(dateUserCanPost.diff(dayjs()))
        // TODO: Formatting a duration only works for durations that are < 24 hours.
        // https://github.com/iamkun/dayjs/issues/641
        // For now, when duration is > 24 hours, just show "X day(s)" instead of HH:mm:ss
        if (t.asDays() > 1) {
            return t.days() + ' day' + (t.days() > 1 ? 's' : '')
        } else {
            return dayjs.utc(t.asMilliseconds()).format('HH:mm:ss')
        }
    },
    replyToSuggestion: state => state.replyToSuggestion,
    isReplyingToSuggestion: state => !!state.replyToSuggestion,
    listFilteringProps: (state, getters) => ({
        listType: state.listType,
        sortDir: state.sortDir,
        channelId: getters.channelId,
    }),
    findSuggestion: state => sid => state.data.find(s => s.id === sid)
}

export default {
    state,
    mutations,
    actions,
    getters
}
